import { useQuery } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'
import { TESTING_DUE_DAYS, daysSince } from '@/lib/testingAnalytics'

export interface AthleteLastTest {
  athlete_id: string
  name: string
  last_test: string | null
  test_count: number
}

export interface DueAthlete extends AthleteLastTest {
  /** Days since last test; null when never tested. */
  days: number | null
}

/**
 * Every athlete overdue for VALD testing, longest-overdue first with
 * never-tested athletes last. Reads the athlete_last_test view, so it's one
 * query rather than pulling every result row.
 */
export function useTestingDue(enabled = true) {
  return useQuery({
    queryKey: ['testing-due'],
    enabled: enabled && supabaseReady,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('athlete_last_test')
        .select('athlete_id,name,last_test,test_count')
      if (error) throw error
      const rows = (data ?? []) as AthleteLastTest[]

      const due: DueAthlete[] = rows
        .map((r) => ({ ...r, days: r.last_test ? daysSince(r.last_test) : null }))
        .filter((r) => r.days == null || r.days >= TESTING_DUE_DAYS)
        .sort((a, b) => {
          // Never-tested go last — they need booking in, not chasing.
          if (a.days == null && b.days == null) return a.name.localeCompare(b.name)
          if (a.days == null) return 1
          if (b.days == null) return -1
          return b.days - a.days
        })

      return {
        due,
        overdue: due.filter((d) => d.days != null).length,
        neverTested: due.filter((d) => d.days == null).length,
        upToDate: rows.length - due.length,
      }
    },
  })
}
